import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { buildAppLink, fetchAppLinksConfig } from '../utils/appLinks';
import {
  ClipboardText,
  Command,
  BookOpen,
  Robot,
  Barcode,
  Clock,
  Users,
  Briefcase,
  Warning,
  CaretRight,
  ArrowsClockwise,
  Info,
  Lightning,
  CheckCircle,
  Timer,
  User,
  ArrowSquareOut,
} from '@phosphor-icons/react';

// Icon mapping
const ICON_MAP = {
  workorder: Briefcase,
  crew: Users,
  canvass: ClipboardText,
  log: BookOpen,
  safety: Robot,
  timesheet: Clock,
  tool: Barcode,
  user: User,
  info: Info,
};

// App color mapping
const APP_COLORS = {
  pcc: 'text-blue-600 bg-blue-100',
  quickcanvass: 'text-emerald-600 bg-emerald-100',
  stationlogbook: 'text-amber-600 bg-amber-100',
  askstewie: 'text-violet-600 bg-violet-100',
  timesheets: 'text-orange-600 bg-orange-100',
  rfid: 'text-slate-600 bg-slate-100',
  getgridgate: 'text-primary bg-primary/10',
};

const APP_LABELS = {
  pcc: 'Command Center',
  quickcanvass: 'Quick Canvass',
  stationlogbook: 'Station Log',
  askstewie: 'AskStewie',
  timesheets: 'Timesheets',
  rfid: 'RFID',
  getgridgate: 'GETGRIDGATE',
};

const formatRelativeTime = (timestamp) => {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return then.toLocaleDateString();
};

export const ActivityFeed = ({ 
  limit = 10, 
  showHeader = true, 
  showFilters = true,
  compact = false,
  onViewMore,
  appLinksConfig: externalConfig 
}) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appFilter, setAppFilter] = useState('all');
  const [summary, setSummary] = useState({ info: 0, warning: 0, urgent: 0, total: 0 });
  const [hasMore, setHasMore] = useState(false);
  const [appLinksConfig, setAppLinksConfig] = useState(externalConfig || { links: {}, navConfig: {} });

  // Fetch app links config if not provided
  useEffect(() => {
    if (!externalConfig) {
      fetchAppLinksConfig(api).then(config => setAppLinksConfig(config));
    } else {
      setAppLinksConfig(externalConfig);
    }
  }, [api, externalConfig]);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/hub/feed?limit=${limit}`;
      if (appFilter !== 'all') {
        url += `&app_filter=${appFilter}`;
      }
      
      const response = await api.get(url);
      setItems(response.data?.items || []);
      setHasMore(response.data?.has_more || false);
    } catch (error) {
      console.error('Failed to fetch activity feed:', error);
    } finally {
      setLoading(false);
    }
  }, [api, limit, appFilter]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await api.get('/hub/feed/summary');
      setSummary(response.data || { info: 0, warning: 0, urgent: 0, total: 0 });
    } catch (error) {
      console.error('Failed to fetch feed summary:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchFeed();
    fetchSummary();
    
    const interval = setInterval(() => {
      fetchFeed();
      fetchSummary();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchFeed, fetchSummary]);

  /**
   * Handle click on activity item - navigate to external app site
   */
  const handleItemClick = (item) => {
    const appKey = item.appKey;
    
    // Extract path from the first link if available
    let returnPath = '/';
    if (item.links && item.links.length > 0) {
      const primaryLink = item.links[0];
      // If link is already absolute URL, use it directly
      if (primaryLink.url && primaryLink.url.startsWith('http')) {
        window.location.href = primaryLink.url;
        return;
      }
      // Otherwise use it as the return path
      returnPath = primaryLink.url || '/';
    }
    
    // Build external app link and navigate
    const link = buildAppLink(appKey, returnPath, appLinksConfig);
    
    if (link.comingSoon) {
      return; // Don't navigate for coming soon apps
    }
    
    if (link.isExternal) {
      if (link.openInNewTab) {
        window.open(link.url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = link.url;
      }
    } else {
      // Fallback to internal navigation
      navigate(link.url);
    }
  };

  const getSeverityIndicator = (severity) => {
    switch (severity) {
      case 'urgent':
        return <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />;
      case 'warning':
        return <div className="w-2 h-2 rounded-full bg-amber-500" />;
      default:
        return null;
    }
  };

  return (
    <Card className="border-slate-200" data-testid="activity-feed">
      {showHeader && (
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lightning size={20} weight="fill" className="text-primary" />
              <CardTitle className="text-base">Recent Activity</CardTitle>
              {summary.warning + summary.urgent > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {summary.warning + summary.urgent}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {showFilters && (
                <Select value={appFilter} onValueChange={setAppFilter}>
                  <SelectTrigger className="w-36 h-8 text-xs" data-testid="activity-filter">
                    <SelectValue placeholder="All Apps" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Apps</SelectItem>
                    <SelectItem value="pcc">Command Center</SelectItem>
                    <SelectItem value="quickcanvass">Quick Canvass</SelectItem>
                    <SelectItem value="stationlogbook">Station Log</SelectItem>
                    <SelectItem value="askstewie">AskStewie</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => { fetchFeed(); fetchSummary(); }}
                disabled={loading}
              >
                <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        </CardHeader>
      )}
      
      <CardContent className={showHeader ? '' : 'pt-6'}>
        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <ArrowsClockwise size={24} className="animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Lightning size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No recent activity</p>
          </div>
        ) : (
          <div className="space-y-1">
            {items.map((item, index) => {
              const IconComponent = ICON_MAP[item.iconKey] || Info;
              const appColor = APP_COLORS[item.appKey] || APP_COLORS.getgridgate;
              const linkInfo = appLinksConfig.links?.[item.appKey];
              const isExternal = linkInfo && linkInfo.url;
              
              return (
                <div
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className={`
                    flex items-start gap-3 p-3 rounded-lg cursor-pointer
                    hover:bg-slate-50 transition-colors group
                    ${item.severity === 'warning' ? 'bg-amber-50/50' : ''}
                    ${item.severity === 'urgent' ? 'bg-red-50/50' : ''}
                  `}
                  data-testid={`activity-item-${index}`}
                >
                  {/* Icon */}
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${appColor}`}>
                    <IconComponent size={18} weight="fill" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {getSeverityIndicator(item.severity)}
                          <p className={`font-medium text-slate-900 truncate ${compact ? 'text-sm' : ''}`}>
                            {item.title}
                          </p>
                        </div>
                        {item.subtitle && (
                          <p className="text-xs text-slate-500 truncate mt-0.5">
                            {item.subtitle}
                          </p>
                        )}
                        {!compact && item.actor?.name && (
                          <p className="text-xs text-slate-400 mt-1">
                            by {item.actor.name}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className="text-xs text-slate-400" title={new Date(item.ts).toLocaleString()}>
                          {formatRelativeTime(item.ts)}
                        </span>
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${appColor} border-0`}>
                            {APP_LABELS[item.appKey] || item.appKey}
                          </Badge>
                          {isExternal && (
                            <ArrowSquareOut size={12} className="text-slate-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Arrow */}
                  <CaretRight 
                    size={16} 
                    className="text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0 mt-2" 
                  />
                </div>
              );
            })}
          </div>
        )}
        
        {/* View More */}
        {(hasMore || onViewMore) && items.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={onViewMore || (() => {})}
            >
              View All Activity
              <CaretRight size={16} className="ml-2" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
